/* eslint-disable max-lines */
import React, { useMemo } from 'react';
import { ProfilePicture, Tooltip } from '@getro/rombo';
import moment from 'moment';
import { Text, Flex, Box } from 'rebass/styled-components';
import { displayCompensationPeriod } from 'helpers/compensation';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import { Circle, Maximize2 } from 'lucide-react';
import { mapSeniorityType } from 'services/contacts/seniorityTypes';
import { trackEvent } from 'helpers/analytics';
import { getAuthor, getExpirationDate } from '../jobHelpers';
import strings from '../../../../strings';
import JobStatus from '../../../atoms/JobStatus';
import JobVisibility from '../../../atoms/JobVisibility';
import JobFeatured from '../jobFeatured';
import { CreateListItem } from '../createListItem';
import { BoostIcon } from '../components/BoostIcon';

export const useTableRows = ({
  jobs,
  onShowJobRulesClick,
  createMatches,
  isCreatingList,
  onListClicked,
  onApplicantsClicked,
  showApplicants,
  hasConnect,
  onLaunchApplicationBoostAgent,
}) => {
  const jobBoastStatuses = useMemo(
    () => [
      {
        id: 'not_boosted',
        label: 'Not boosted',
        background: 'neutral.20',
        actionText: 'Boost job',
        action: (job) => {
          trackEvent('application_boost:boost_job_click');
          onLaunchApplicationBoostAgent({ view: 'launch', job });
        },
      },
      {
        id: 'ready_to_launch',
        label: 'Ready to launch',
        color: 'blue.700',
        background: '#EDF0FD',
        actionText: 'Open agent',
        action: (job) => {
          trackEvent('application_boost:open_boost_agent', {
            agent: 'application_boost',
          });
          onLaunchApplicationBoostAgent({ view: 'boost', job });
        },
      },
      {
        id: 'needs_calibration',
        label: 'Needs calibration',
        color: 'blue.700',
        background: '#EDF0FD',
        actionText: 'Calibrate',
        action: (job) => {
          trackEvent('application_boost:open_boost_agent', {
            agent: 'calibration-selection',
          });
          onLaunchApplicationBoostAgent({ view: 'calibration-selection', job });
        },
      },
      {
        id: 'calibrating',
        label: 'Preparing boost',
        color: 'text.main',
        background: 'neutral.100',
        actionText: null,
        action: null,
        tooltip: "We're getting ready to boost your job. It'll be ready in a few minutes.",
      },
      {
        id: 'in_progress',
        label: 'In progress',
        icon: BoostIcon,
        color: '',
        background: 'purple.100',
        actionText: 'Open agent',
        sx: {
          display: 'flex',
          flexShrink: 0,
          alignItems: 'center',
          gap: '8px',
          borderRadius: '4px',
          background:
            'linear-gradient(78deg, rgba(73, 0, 255, 0.08) 0%, rgba(212, 0, 255, 0.08) 50%, rgba(255, 146, 87, 0.08) 90%, rgba(73, 0, 255, 0.08) 100%), #FFF',
          backgroundSize: '200% 200%',
          animation: 'gradient 2.5s ease-in infinite',
          px: '8px',
          height: '24px',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '4px',
            padding: '1px',
            background: 'linear-gradient(78deg, #4900FF 0%, #D400FF 50%, #FF9257 90%, #4900FF 100%), #FFF',
            backgroundSize: '200% 200%',
            animation: 'gradient 2.5s ease-in infinite',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude',
            overflow: 'hidden',
          },
          '@keyframes gradient': {
            '0%': {
              backgroundPosition: '200% 50%',
            },
            '100%': {
              backgroundPosition: '0% 50%',
            },
          },
        },
        action: (job) => {
          trackEvent('application_boost:open_boost_agent_click', {
            agent: 'application_boost',
          });
          onLaunchApplicationBoostAgent({ view: 'boost', job });
        },
      },
      {
        id: 'finished',
        label: 'Completed',
        color: 'green.700',
        background: 'green.20',
        actionText: 'Open agent',
        action: (job) => {
          onLaunchApplicationBoostAgent({ view: 'boost', job });
        },
      },
    ],
    [onLaunchApplicationBoostAgent],
  );

  return useMemo(
    () =>
      jobs.map((job) => {
        const { name: companyName } = job.organization;
        const { id: authorId } = job.author || {};
        const { selected } = job;
        const jobBoastStatus =
          jobBoastStatuses.find((status) => status.id === job.sourcingCampaign?.status) ?? jobBoastStatuses[0];

        const shouldExcludeForOurAgents = ['executive', 'cxo', 'vice_president'].includes(job.seniority);
        const isJobNotVisibleOrHidden = ['hidden', 'not_visible', 'hidden_forced'].includes(job.visibility);

        return {
          original: job,
          id: job.id,
          slug: job.slug,
          authorId,
          selected,
          rowId: job.id,
          getRowHeight: () => (job.sharedLists?.length > 0 ? 34 * job.sharedLists.length + 12 : 48),
          title: (
            <Flex width="100%" alignItems="flex-start" justifyContent="flex-start">
              <Flex flexDirection="column">
                <Text
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  color="text.main"
                  fontWeight="500"
                  fontSize={[1]}
                >
                  {job.title}
                </Text>
              </Flex>
            </Flex>
          ),
          company: (
            <Flex alignItems="center" sx={{ gap: '8px' }} color="text.main">
              <Box width="24px" height="24px" sx={{ flexShrink: 0 }}>
                <ProfilePicture variant="square" imageUrl={job.organization.logoUrl} title={companyName} />
              </Box>
              <Text color="text.main" fontSize="14px">
                {companyName}
              </Text>
            </Flex>
          ),
          applicationBoostAgent: (
            <Flex alignItems="center" sx={{ gap: '8px' }} color="text.main">
              {isJobNotVisibleOrHidden && (
                <Box
                  sx={{
                    color: 'text.subtle',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '24px',
                    fontStyle: 'italic',
                  }}
                >
                  Not available for non-visible jobs
                </Box>
              )}
              {!isJobNotVisibleOrHidden && shouldExcludeForOurAgents && (
                <Tooltip
                  content="AI boosting works best for roles up to Director level. Executive roles require a more tailored approach."
                  position="top-start"
                  trigger={
                    <Box
                      sx={{
                        color: 'text.subtle',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '24px',
                        fontStyle: 'italic',
                      }}
                    >
                      Not eligible
                    </Box>
                  }
                />
              )}
              {!isJobNotVisibleOrHidden && !shouldExcludeForOurAgents && (
                <DynamicTooltip
                  useTooltip={Boolean(jobBoastStatus.tooltip)}
                  content={jobBoastStatus.tooltip}
                  position="top-start"
                  sx={{
                    '.tooltip-arrow': {
                      left: '-50px !important',
                    },
                  }}
                >
                  <Flex alignItems="center" sx={{ gap: '8px' }}>
                    {jobBoastStatus.id === 'not_boosted' && (
                      <Flex
                        alignItems="center"
                        sx={{ gap: '4px', cursor: 'pointer', textDecoration: 'underline', color: 'text.dark' }}
                        onClick={() => {
                          if (jobBoastStatus?.action) {
                            jobBoastStatus?.action(job);
                          }
                        }}
                      >
                        Boost job
                        <Box
                          width="16px"
                          height="16px"
                          sx={{
                            flexShrink: 0,
                          }}
                        >
                          <BoostIcon />
                        </Box>
                      </Flex>
                    )}
                    {jobBoastStatus.id === 'in_progress' && (
                      <>
                        <Box sx={jobBoastStatus.sx}>
                          <Text
                            as="span"
                            fontSize="14px"
                            color="text.dark"
                            sx={{
                              fontWeight: '500',
                              lineHeight: '24px',
                            }}
                          >
                            {jobBoastStatus.label}
                          </Text>
                          <BoostIcon />
                        </Box>
                        <Box
                          alignItems="center"
                          sx={{
                            opacity: 0,
                            position: 'relative',
                            top: '0',
                            left: '-24px',
                            transition: 'all 0.3s ease-in-out',
                            '.ag-row-hover &': {
                              opacity: 1,
                              top: '0',
                              left: 0,
                            },
                          }}
                        >
                          <Text
                            sx={{
                              lineHeight: '24px',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              color: 'text.dark',
                            }}
                            onClick={() => {
                              if (jobBoastStatus?.action) {
                                jobBoastStatus?.action(job);
                              }
                            }}
                            fontSize="14px"
                          >
                            {jobBoastStatus?.actionText}
                          </Text>
                        </Box>
                      </>
                    )}
                    {jobBoastStatus.id === 'calibrating' && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexShrink: 0,
                          alignItems: 'center',
                          gap: '4px',
                          borderRadius: '4px',
                          bg: jobBoastStatus.background || 'neutral.20',
                          px: '8px',
                          height: '24px',
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                          <Box
                            as={Circle}
                            size={6}
                            sx={{
                              color: jobBoastStatus.color,
                              background: jobBoastStatus.color,
                              fill: jobBoastStatus.color,
                            }}
                          />
                        </svg>
                        <Text
                          as="span"
                          fontSize="14px"
                          color={jobBoastStatus.color || 'text.main'}
                          sx={{
                            fontWeight: '500',
                            lineHeight: '24px',
                          }}
                        >
                          {jobBoastStatus.label}
                        </Text>
                      </Box>
                    )}
                    {!['not_boosted', 'in_progress', 'calibrating'].includes(jobBoastStatus.id) && (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            flexShrink: 0,
                            alignItems: 'center',
                            gap: '4px',
                            borderRadius: '4px',
                            bg: jobBoastStatus.background || 'neutral.20',
                            px: '8px',
                            height: '24px',
                            ...(jobBoastStatus.sx ? jobBoastStatus.sx : {}),
                          }}
                        >
                          {jobBoastStatus.id !== 'finished' && (
                            <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                              <circle cx="3" cy="3" r="3" fill="#0F388A" />
                            </svg>
                          )}
                          <Text
                            as="span"
                            fontSize="14px"
                            color={jobBoastStatus.color || 'text.main'}
                            sx={{
                              fontWeight: '500',
                              lineHeight: '24px',
                            }}
                          >
                            {jobBoastStatus.label}
                          </Text>
                        </Box>

                        <Box
                          alignItems="center"
                          sx={{
                            opacity: 0,
                            position: 'relative',
                            left: '-24px',
                            transition: 'all 0.3s ease-in-out',
                            '.ag-row-hover &': {
                              opacity: 1,
                              left: 0,
                            },
                          }}
                        >
                          <Text
                            sx={{
                              lineHeight: '24px',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              color: 'text.dark',
                            }}
                            onClick={() => {
                              if (jobBoastStatus?.action) {
                                jobBoastStatus?.action(job);
                              }
                            }}
                            fontSize="14px"
                          >
                            {jobBoastStatus?.actionText}
                          </Text>
                        </Box>
                      </>
                    )}
                  </Flex>
                </DynamicTooltip>
              )}
            </Flex>
          ),
          jobFunction: (
            <Text
              sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              color="text.main"
            >
              {job.jobFunctions && job.jobFunctions.length ? job.jobFunctions.map((item) => item.name).join(', ') : '-'}
            </Text>
          ),
          seniority: (
            <Text fontSize={[1]} color="text.main">
              {job.seniority ? mapSeniorityType(job.seniority) : '-'}
            </Text>
          ),
          source: (
            <Text fontSize={[1]} color="text.main">
              {strings.jobs.job_sources_for_admin_portal[job.source]}
            </Text>
          ),
          status: (
            <Flex fontSize={[1]} alignItems="flex-start">
              <JobStatus job={job} />
            </Flex>
          ),
          visibility: (
            <Flex fontSize={[1]}>
              <JobVisibility visibility={job.visibility} onShowJobRulesClick={onShowJobRulesClick} />
            </Flex>
          ),
          featured: (
            <Flex fontSize={[1]}>
              <JobFeatured isFeatured={job.featured === 'featured'} />
            </Flex>
          ),
          list: (
            <Flex minWidth="280px" fontSize={[1]} sx={{ gap: '8px' }}>
              <CreateListItem
                onListClicked={onListClicked}
                job={{
                  id: job.id,
                  ...job,
                }}
                createMatches={createMatches}
                isCreatingList={isCreatingList}
              />
            </Flex>
          ),
          applicants: (
            <DynamicTooltip
              sx={{
                '.tooltip-arrow:after': { ml: '-25px' },
              }}
              useTooltip={job.jobApplicantsCount && showApplicants}
              position="top-start"
              content="See applicants in Contacts screen"
              trigger={
                <Flex
                  justifyContent="space-between"
                  flex="1"
                  alignItems="center"
                  fontSize={[1]}
                  onClick={() => {
                    if (job.jobApplicantsCount && hasConnect) {
                      onApplicantsClicked(job);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover #see-applicants-in-contacts-screen': { display: 'flex' },
                  }}
                >
                  <Text color="text">{typeof job.jobApplicantsCount === 'number' ? job.jobApplicantsCount : ' -'}</Text>

                  {showApplicants && job.jobApplicantsCount > 0 && (
                    <Box
                      id="see-applicants-in-contacts-screen"
                      sx={{
                        display: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        padding: '4px',
                        width: '20px',
                        height: '20px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          background: 'neutral.30',
                        },
                      }}
                    >
                      <Box as={Maximize2} size={16} sx={{ color: 'text.main' }} />
                    </Box>
                  )}
                </Flex>
              }
            />
          ),
          createdBy: (
            <Flex width={[1]} flexDirection="column">
              <Text fontSize={[1]} color="text.main">
                {getAuthor(job)}
              </Text>
            </Flex>
          ),
          createdAt: (
            <Flex width={[1]} flexDirection="column">
              <Text fontSize={[1]} color="text.main">
                {moment(job.createdAt).format('D MMM YYYY')}
              </Text>
            </Flex>
          ),
          expirationDate: (
            <Flex width={[1]}>
              <Text fontSize={[1]} color="text.main">
                {getExpirationDate(job)}
              </Text>
            </Flex>
          ),
          location: (
            <Text fontSize={[1]} color="text.main">
              {job.locations && job.locations.length ? job.locations?.join(', ') : '-'}
            </Text>
          ),
          compensation: (
            <Text fontSize={[1]} color="text.main">
              {job.compensationCurrency &&
              job.compensationAmountMinCents &&
              job.compensationAmountMaxCents &&
              job.compensationPeriod
                ? displayCompensationPeriod({
                    currency: job.compensationCurrency,
                    amountMinCents: job.compensationAmountMinCents,
                    amountMaxCents: job.compensationAmountMaxCents,
                    offersEquity: job.compensationOffersEquity,
                    period: job.compensationPeriod,
                  })
                : '-'}
            </Text>
          ),
        };
      }),
    [
      jobs,
      jobBoastStatuses,
      onShowJobRulesClick,
      onListClicked,
      createMatches,
      isCreatingList,
      showApplicants,
      hasConnect,
      onApplicantsClicked,
    ],
  );
};
